import { Server } from "@wajeez/wajeez.microservices";
import { getPublicMediaUrl } from "@modules/api/util/mediaHelper";
import ContentCarousel from "@components/items/display/ContentCarousel";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";
import SkillCard from "@components/items/display/SkillCard";
import { GenericLoading } from "@components/items/common/GenericLoading";
import { clearQuizActions } from "@lib/stores";
import { useIsLoadingVisibile } from "@utils/hooks/useIsLoadingVisible";
import { HorizontalListSkeleton } from "./HorizontalListSkeleton";
import { useLanguage } from "@modules/localizations/LanguageContext";
import { OnItemPress } from "@modules/content/hooks/useComponentData";

export interface SkillCardListParameters {
  title: string | null | undefined;
  subTitle: string | null | undefined;
  seeAllText: string | null | undefined;
}

export interface SkillCardListItemParameters {
  image: string | null | undefined;
  icon: string | null | undefined;
  description: string | null | undefined;
}

type ItemParams = {
  id: string | undefined;
  title: string | undefined;
  authors: string[] | undefined;
} & {
  [K in keyof SkillCardListItemParameters]?:
    | SkillCardListItemParameters[K]
    | undefined;
};

interface SkillCardListProps {
  onItemPress: OnItemPress<ItemParams>;
  contents:
    | (Server.ContentEntityOutput & {
        isLocked: boolean | undefined;
        lockType: Server.LockType | undefined;
        progress: number | undefined;
        completedItems: number | undefined;
      })[]
    | undefined;
  isLoading: boolean;
  parameterValues: SkillCardListParameters;
  items: ItemParams[];
}

export const SkillCardList = ({
  onItemPress,
  contents,
  isLoading,
  parameterValues,
  items,
}: SkillCardListProps) => {
  const navigate = useAppNavigate();

  const { direction } = useLanguage();

  const { title, seeAllText, subTitle } = parameterValues;

  const isLoadingVisible = useIsLoadingVisibile(
    isLoading && (!contents || Boolean(contents && !contents.length))
  );

  return (
    <div
      className={`relative ${(isLoadingVisible || isLoading) && "h-[350px]"}`}
    >
      <div
        className={`absolute top-0 w-full px-10 ltr:left-0 rtl:right-0 ${
          isLoadingVisible && "z-10 bg-white dark:bg-black"
        }`}
      >
        <SkillCardListLoading isLoading={isLoadingVisible} />
      </div>
      {contents && contents.length ? (
        <div className="flex w-full flex-col items-center justify-center gap-y-3 px-10 pb-[35px]">
          <div className="flex w-full items-center justify-between">
            <div className={`text-[20px] font-bold dark:text-white`}>
              {title}
            </div>
            {/* <div className={`dark:text-white text-[20px] font-normal`}>
          {seeAllText}
        </div> */}
          </div>
          <div className={`w-full text-[16px] font-normal text-[#898A8D]`}>
            {subTitle}
          </div>
          <div className="flex w-full items-center justify-start">
            <ContentCarousel
              className="w-[95%] sm:max-w-[700px] md:max-w-[1150px] xl:max-w-[1500px]"
              itemClassName="sm:basis-1/3 md:basis-1/5 xl:basis-1/6"
              options={{
                direction,
              }}
              items={items.map((item, i) => {
                const content = contents.find((c) => c.id === item.id);

                return (
                  <SkillCard
                    image={getPublicMediaUrl(item.image)}
                    icon={getPublicMediaUrl(item.icon)}
                    title={item.title ?? ""}
                    completedSummaries={content?.completedItems || 0}
                    discription={item.description || ""}
                    progress={content?.progress || 0}
                    totalDuration={content?.durationMS || 0}
                    totalSummaries={content?.chaptersCount || 0}
                    onCardClick={() => {
                      clearQuizActions();

                      const content = contents.find((c) => c.id === item.id);

                      content &&
                        onItemPress(content, item, {
                          isLocked: content.isLocked,
                        });
                    }}
                  />
                );
              })}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

const SkillCardListLoading = ({ isLoading }: { isLoading: boolean }) => {
  return isLoading ? <HorizontalListSkeleton /> : null;
};
